import { Ban, Check, Clock, Route, TriangleAlert, Unplug } from "lucide-react";
import type { ReactNode } from "react";

type StatusPillVariant =
  "active" | "suspended" | "online" | "offline" | "idle" | "warning" | "routed";

const variantClasses: Record<StatusPillVariant, string> = {
  active: "border-success/35 bg-success/10 text-success",
  suspended: "border-danger/40 bg-danger/10 text-danger",
  online: "border-success/35 bg-success/10 text-success",
  offline: "border-danger/40 bg-danger/10 text-danger",
  idle: "border-muted bg-muted/25 text-foreground/70",
  warning: "border-warning/40 bg-warning/10 text-warning",
  routed: "border-muted bg-muted/25 text-foreground/70",
};

function Glyph({ variant }: { variant: StatusPillVariant }) {
  if (variant === "online") {
    return (
      <span
        aria-hidden="true"
        className="mx-0.5 h-[7px] w-[7px] shrink-0 rounded-full bg-current motion-safe:animate-pulse"
      />
    );
  }

  if (variant === "offline") {
    return (
      <Unplug className="h-3 w-3 shrink-0" strokeWidth={3} aria-hidden="true" />
    );
  }

  if (variant === "warning") {
    return (
      <TriangleAlert
        className="h-3 w-3 shrink-0"
        strokeWidth={3}
        aria-hidden="true"
      />
    );
  }

  if (variant === "routed") {
    return (
      <Route className="h-3 w-3 shrink-0" strokeWidth={3} aria-hidden="true" />
    );
  }

  if (variant === "idle") {
    return (
      <Clock className="h-3 w-3 shrink-0" strokeWidth={3} aria-hidden="true" />
    );
  }

  if (variant === "active") {
    return (
      <Check
        className="h-3 w-3 shrink-0"
        strokeWidth={3.5}
        aria-hidden="true"
      />
    );
  }

  return (
    <Ban className="h-3 w-3 shrink-0" strokeWidth={3} aria-hidden="true" />
  );
}

export function StatusPill({
  variant,
  children,
}: {
  variant: StatusPillVariant;
  children: ReactNode;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border py-0.5 pl-1.5 pr-2.5 text-xs font-medium ${variantClasses[variant]}`}
    >
      <Glyph variant={variant} />
      {children}
    </span>
  );
}
